import {
  CommandFunction,
  EditorState,
  MarkType,
  command,
  extension,
  PlainExtension,
} from 'remirror';
import { TextSelection } from '@remirror/pm/state';
import { CuesApplied } from 'services/eventTrackingServiceTypes';
import { escapeXml, escapeXmlAttr } from '../utils/editorXml';

export type CueType = 'tempo' | 'pitch' | 'loudness';

type CueCommandsOptions = object;

const defaultOptions: Required<CueCommandsOptions> = {};

@extension({
  defaultOptions,
  staticKeys: [],
  handlerKeys: [],
  customHandlerKeys: [],
})
export class CueCommandsExtension extends PlainExtension<CueCommandsOptions> {
  get name() {
    return 'cueCommands' as const;
  }

  private getMarkType(state: EditorState, type: CueType): MarkType | null {
    switch (type) {
      case 'tempo':
        return state.schema.marks.tempo ?? null;
      case 'pitch':
        return state.schema.marks.pitch ?? null;
      case 'loudness':
        return state.schema.marks.loudness ?? null;
      default:
        return null;
    }
  }

  private isWhitespace(char: string) {
    return /\s/.test(char);
  }

  private isWordChar(char: string) {
    return /[\p{L}\p{N}_]/u.test(char);
  }

  private isPunct(char: string) {
    return !this.isWhitespace(char) && !this.isWordChar(char);
  }

  private tokenClass(char: string) {
    if (!char) return 'boundary';
    if (this.isWhitespace(char)) return 'space';
    if (this.isWordChar(char)) return 'word';
    return 'punct';
  }

  private isBoundary(state: EditorState, pos: number) {
    const before = state.doc.textBetween(Math.max(0, pos - 1), pos);
    const after = state.doc.textBetween(pos, pos + 1);
    const cb = this.tokenClass(before);
    const ca = this.tokenClass(after);
    return (
      cb !== ca ||
      cb === 'space' ||
      ca === 'space' ||
      before === '' ||
      after === ''
    );
  }

  private clampToTokenBoundaries(state: EditorState, from: number, to: number) {
    let f = from;
    let t = to;
    const max = state.doc.nodeSize - 2;
    while (f > 0 && !this.isBoundary(state, f)) f -= 1;
    while (t < max && !this.isBoundary(state, t)) t += 1;
    return [f, t] as const;
  }

  private trimEdgeWhitespace(state: EditorState, from: number, to: number) {
    if (to <= from) return [from, to] as const;
    const text = state.doc.textBetween(from, to, '\n', '\n');
    if (!text) return [from, to] as const;
    let lead = 0;
    while (lead < text.length && /\s/.test(text[lead]!)) lead += 1; // Test for whitespace at the start of the text
    let trail = 0;
    while (
      trail < text.length - lead &&
      /\s/.test(text[text.length - 1 - trail]!) // Test for whitespace at the end of the text
    )
      trail += 1;
    const nf = from + lead;
    const nt = to - trail;
    return [nf, nt] as const;
  }

  private getSchemaMark(state: EditorState, type: CueType): MarkType | null {
    return (
      (state.schema.marks as Record<string, MarkType | undefined>)[type] ?? null
    );
  }

  private excludeEdgeSpaces(
    state: EditorState,
    tr: Parameters<CommandFunction>[0]['tr'],
    type: CueType,
    from: number,
    to: number
  ) {
    const markType = this.getSchemaMark(state, type);
    if (!markType) return;

    const contentSize = state.doc.content.size; // same as nodeSize - 2
    const beforePos = Math.max(0, Math.min(from - 1, contentSize - 1));
    const afterPos = Math.max(0, Math.min(to, contentSize - 1));

    if (beforePos + 1 <= contentSize) {
      const beforeChar = state.doc.textBetween(beforePos, beforePos + 1);
      if (
        beforeChar === ' ' &&
        state.doc.rangeHasMark(beforePos, beforePos + 1, markType)
      ) {
        tr.removeMark(beforePos, beforePos + 1, markType);
      }
    }

    if (afterPos + 1 <= contentSize) {
      const afterChar = state.doc.textBetween(afterPos, afterPos + 1);
      if (
        afterChar === ' ' &&
        state.doc.rangeHasMark(afterPos, afterPos + 1, markType)
      ) {
        tr.removeMark(afterPos, afterPos + 1, markType);
      }
    }
  }

  @command()
  setCue(type: CueType, value: string): CommandFunction {
    return ({ state, tr, dispatch }) => {
      const { from, to, empty } = state.selection;
      if (empty) return false;

      const markType = this.getMarkType(state, type);
      if (!markType) return false;
      let [f, t] = this.trimEdgeWhitespace(state, from, to);
      if (t <= f) return false;
      [f, t] = this.clampToTokenBoundaries(state, f, t);

      tr.addMark(f, t, markType.create({ value }));
      // Ensure spaces between same-type regions remain unmarked
      this.excludeEdgeSpaces(state, tr, type, f, t);
      dispatch?.(tr.scrollIntoView());
      return true;
    };
  }

  @command()
  setMultipleCues(cues: Record<CueType, string>): CommandFunction {
    return ({ state, tr, dispatch }) => {
      const { from, to, empty } = state.selection;
      if (empty) return false;
      let [f, t] = this.trimEdgeWhitespace(state, from, to);
      if (t <= f) return false;
      [f, t] = this.clampToTokenBoundaries(state, f, t);
      const order: CueType[] = ['pitch', 'loudness'];

      for (const cueType of order) {
        const markType = this.getMarkType(state, cueType);
        if (markType) {
          const raw = cues[cueType];
          const isZeroOrEmpty =
            raw === undefined ||
            raw === null ||
            raw === '' ||
            raw === '0' ||
            raw === '0.0';

          if (!isZeroOrEmpty) {
            tr.addMark(f, t, markType.create({ value: String(raw) }));
            // Ensure spaces between same-type regions remain unmarked
            this.excludeEdgeSpaces(state, tr, cueType, f, t);
          } else {
            tr.removeMark(f, t, markType);
          }
        }
      }

      // Tempo is a special case - it defaults to 1 if not set
      if (cues.tempo) {
        const markType = this.getMarkType(state, 'tempo');

        const raw = cues.tempo;
        const isOneOrEmpty =
          raw === undefined ||
          raw === null ||
          raw === '' ||
          raw === '1' ||
          raw === '1.0';

        if (markType) {
          if (!isOneOrEmpty) {
            tr.addMark(f, t, markType.create({ value: String(cues.tempo) }));
            // Ensure spaces between same-type regions remain unmarked
            this.excludeEdgeSpaces(state, tr, 'tempo', f, t);
          } else {
            tr.removeMark(f, t, markType);
          }
        }
      }

      dispatch?.(tr.scrollIntoView());
      return true;
    };
  }

  @command()
  clearCues(): CommandFunction {
    return ({ state, tr, dispatch }) => {
      const docSize = state.doc.content.size;
      if (docSize === 0) {
        return false;
      }
      const textStart = 1;
      const textEnd = docSize - 1;
      if (textEnd <= textStart) {
        return false;
      }
      tr.removeMark(textStart, textEnd, state.schema.marks.tempo);
      tr.removeMark(textStart, textEnd, state.schema.marks.pitch);
      tr.removeMark(textStart, textEnd, state.schema.marks.loudness);
      tr.removeMark(textStart, textEnd, state.schema.marks.pause);
      dispatch?.(tr.scrollIntoView());
      return true;
    };
  }

  @command()
  removeCue(type: CueType): CommandFunction {
    return ({ state, tr, dispatch }) => {
      const { from, to, empty } = state.selection;
      if (empty) return false;

      const mt = this.getMarkType(state, type);
      if (!mt) return false;
      let [f, t] = this.trimEdgeWhitespace(state, from, to);
      if (t <= f) return false;
      [f, t] = this.clampToTokenBoundaries(state, f, t);

      tr.removeMark(f, t, mt);

      dispatch?.(tr.scrollIntoView());
      return true;
    };
  }

  @command()
  selectAllText(): CommandFunction {
    return ({ state, tr, dispatch }) => {
      const docSize = state.doc.content.size;

      if (docSize === 0) {
        return false;
      }
      const textStart = 1;
      const textEnd = docSize - 1;

      if (textEnd <= textStart) {
        return false;
      }

      if (textStart < 0 || textEnd > docSize) {
        return false;
      }

      const selection = TextSelection.create(state.doc, textStart, textEnd);
      tr.setSelection(selection);

      dispatch?.(tr.scrollIntoView());
      return true;
    };
  }

  // Helper methods for editor state operations

  /**
   * Gets the selected text from the current selection
   */
  getSelectedText(state: EditorState): string {
    const { from, to } = state.selection;
    return state.doc.textBetween(from, to, '\n', '\n');
  }

  /**
   * Gets the formatted text as xml
   */
  getFormattedText(state: EditorState): string {
    return serializeToXml(state);
  }
}

export function getEditorPlainText(state: EditorState): string {
  return state.doc.textBetween(0, state.doc.content.size, '\n', '\n');
}

export function getCuesAppliedToDocument(state: EditorState): CuesApplied {
  const cuesApplied = {
    loudness: false,
    pace: false,
    pause: false,
    pitch: false,
  };

  const { marks } = state.schema;

  state.doc.descendants(node => {
    node.marks.forEach(mark => {
      if (mark.type === marks.pitch && !cuesApplied.pitch) {
        cuesApplied.pitch = true;
      }
      if (mark.type === marks.tempo && !cuesApplied.pace) {
        cuesApplied.pace = true;
      }
      if (mark.type === marks.loudness && !cuesApplied.loudness) {
        cuesApplied.loudness = true;
      }
      if (mark.type === marks.pause && !cuesApplied.pause) {
        cuesApplied.pause = true;
      }
    });
  });

  return cuesApplied;
}

export function getCurrentCueValues(
  state: EditorState
): Record<CueType, number | 'mixed'> {
  const types: CueType[] = ['tempo', 'pitch', 'loudness'];
  const marksByName = state.schema.marks as Record<string, MarkType>;

  const valueSets: Record<CueType, Set<string>> = {
    tempo: new Set<string>(),
    pitch: new Set<string>(),
    loudness: new Set<string>(),
  };

  const { from, to, empty } = state.selection;

  if (!empty) {
    state.doc.nodesBetween(from, to, node => {
      const { isText } = node as unknown as { isText?: boolean };
      if (isText) {
        for (const t of types) {
          const m = node.marks.find(mm => mm.type === marksByName[t]);
          if (m && m.attrs && m.attrs.value != null) {
            valueSets[t].add(String(m.attrs.value));
          }
        }
      }
      return true;
    });
  }

  const result: Record<CueType, number | 'mixed'> = {
    tempo: 1, // Tempo Defaults to 1
    pitch: 0,
    loudness: 0,
  };

  for (const t of types) {
    const set = valueSets[t];

    // No marks found - defaults to 1 for tempo, 0 for pitch and loudness
    if (set.size === 0) {
      result[t] = t === 'tempo' ? 1 : 0; // Tempo Defaults to 1
    }
    // One mark found - parse the value
    else if (set.size === 1) {
      const only = Array.from(set)[0]!;
      const num = parseFloat(only);
      result[t] = Number.isFinite(num) ? num : 0;
      if (t === 'tempo') {
        result[t] = Number.isFinite(num) ? num : 1;
      }
    }
    // Multiple marks found - mixed
    else {
      result[t] = 'mixed';
    }
  }

  return result;
}

export function serializeCueFormattedText(state: EditorState): string {
  const result: string[] = [];
  const order: CueType[] = ['tempo', 'pitch', 'loudness'];

  state.doc.descendants((node, pos, parent) => {
    // Add newline between top-level blocks
    if (node.isBlock && parent && parent.type && parent.type.name === 'doc') {
      if (result.length > 0) result.push('\n');
      return true;
    }

    if (node.isText) {
      const nodeMarks = node.marks || [];
      const cueMarks = nodeMarks.filter(m =>
        ['tempo', 'pitch', 'loudness'].includes(m.type.name)
      );

      const sorted = cueMarks
        .map(m => ({
          type: m.type.name as CueType,
          value: String(m.attrs?.value ?? ''),
        }))
        .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));

      const pauseMark = nodeMarks.find(m => m.type.name === 'pause');

      const textContent = node.text || '';
      const leadingWhitespace = textContent.match(/^\s+/)?.[0] || '';
      const trailingWhitespace = textContent.match(/\s+$/)?.[0] || '';
      const trimmedText = sorted.length > 0 ? textContent.trim() : textContent;
      const escapedText = escapeXml(trimmedText);

      if (!escapedText) {
        result.push(' ');
        return true;
      }

      const phoneticMark = nodeMarks.find(
        m => m.type.name === 'phonetic' && m.attrs?.enabled === true
      );

      const phoneticValue = phoneticMark
        ? String(phoneticMark.attrs?.replacement ?? '')
        : '';

      if (sorted.length > 0 || phoneticMark?.attrs.isRespelling) {
        result.push(leadingWhitespace);
      }

      if (pauseMark) {
        const pauseValue = pauseMark.attrs?.value ?? '';
        result.push(
          `<tempo value="${escapeXmlAttr(pauseValue)}"><loudness value="-15">`
        );
      }

      for (const m of sorted) {
        result.push(`<${m.type} value="${escapeXmlAttr(m.value)}">`);
      }

      // Respell tag must always be the most inner cue.
      if (phoneticMark && phoneticMark.attrs?.isRespelling) {
        result.push(`<respell value="${escapeXmlAttr(phoneticValue)}">`);
      }

      if (phoneticMark && !phoneticMark.attrs?.isRespelling) {
        // Word replacements are emitted as plain spoken text, not attributes.
        result.push(escapeXml(phoneticValue));
      } else {
        result.push(escapedText);
      }

      if (phoneticMark && phoneticMark.attrs?.isRespelling) {
        result.push(`</respell>`);
      }

      for (let i = sorted.length - 1; i >= 0; i -= 1) {
        result.push(`</${sorted[i]!.type}>`);
      }

      if (pauseMark) {
        result.push('</loudness></tempo>');
      }

      if (sorted.length > 0 || phoneticMark?.attrs.isRespelling) {
        result.push(trailingWhitespace);
      }
    }
    return true;
  });

  return result.join('');
}

export function serializeToXml(state: EditorState): string {
  const result: string[] = [];
  const order: CueType[] = ['tempo', 'pitch', 'loudness'];

  state.doc.descendants((node, pos, parent) => {
    // Add newline between top-level blocks
    if (node.isBlock && parent && parent.type && parent.type.name === 'doc') {
      if (result.length > 0) result.push('\n');
      return true;
    }

    if (!node.isText) return true;

    const nodeMarks = node.marks || [];
    const cueMarks = nodeMarks.filter(m =>
      ['tempo', 'pitch', 'loudness'].includes(m.type.name)
    );

    const sorted = cueMarks
      .map(m => ({
        type: m.type.name as CueType,
        value: String(m.attrs?.value ?? ''),
      }))
      .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));

    const pauseMark = nodeMarks.find(m => m.type.name === 'pause');

    const textContent = node.text || '';
    const leadingWhitespace = textContent.match(/^\s+/)?.[0] || '';
    const trailingWhitespace = textContent.match(/\s+$/)?.[0] || '';
    const trimmedText = sorted.length > 0 ? textContent.trim() : textContent;
    const escapedText = escapeXml(trimmedText);

    if (!escapedText) {
      result.push(' ');
      return true;
    }

    const phoneticMark = nodeMarks.find(m => m.type.name === 'phonetic');

    if (sorted.length > 0 || phoneticMark?.attrs.isRespelling) {
      result.push(leadingWhitespace);
    }

    if (pauseMark) {
      const pauseValue = pauseMark.attrs?.value ?? '';
      result.push(
        `<tempo value="${escapeXmlAttr(pauseValue)}"><loudness value="-15">`
      );
    }

    for (const m of sorted) {
      result.push(`<${m.type} value="${escapeXmlAttr(m.value)}">`);
    }

    // Respell tag must always be the most inner cue.
    if (phoneticMark) {
      const value = escapeXmlAttr(
        String(phoneticMark.attrs?.replacement ?? '')
      );
      const type = phoneticMark.attrs?.isRespelling ? 'respell' : 'word';
      const source = escapeXmlAttr(String(phoneticMark.attrs?.source ?? ''));
      const enabled = phoneticMark.attrs?.enabled ? 'true' : 'false';
      const entryId = String(phoneticMark.attrs?.entryId ?? '');
      const entryIdAttr = entryId ? ` entryId="${escapeXmlAttr(entryId)}"` : '';
      result.push(
        `<replacement value="${value}" type="${type}" source="${source}" enabled="${enabled}"${entryIdAttr}>`
      );
    }

    result.push(escapedText);

    if (phoneticMark) {
      result.push('</replacement>');
    }

    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      result.push(`</${sorted[i]!.type}>`);
    }

    if (pauseMark) {
      result.push('</loudness></tempo>');
    }

    if (sorted.length > 0 || phoneticMark?.attrs.isRespelling) {
      result.push(trailingWhitespace);
    }
  });

  return result.join('');
}
